import {LoginDestinationStore} from '../helper/LoginDestinationStore';
import {Injectable, NgZone} from '@angular/core';
import {from, of, Observable, BehaviorSubject, combineLatest, defer} from 'rxjs';
import {concatMap, filter, take, shareReplay, timeout} from 'rxjs/operators';
import * as firebase from 'firebase/app';
import {AngularFireAuth} from '@angular/fire/auth';
import {Router} from '@angular/router';
import {ConversionService} from './ConversionService';
import {WaihonaUser} from '../domain/user/WaihonaUser';
import {WaihonaUserService} from './WaihonaUserService';
import {WaihonaUserRef} from '../domain/user/WaihonaUserRef';
import {AuthFunctions} from './functions/AuthFunctions';
import {NGXLogger} from 'ngx-logger';
import {DeviceDetectorService} from 'ngx-device-detector';
import {CookieService} from 'ngx-cookie-service';

@Injectable({providedIn:'root'})
export class AuthService {
    public isHandlingLocalSelfAuth$:BehaviorSubject<boolean> = this.authFunctions._isHandlingLocalSelfAuth$;
    public isAuthenticated_b$:Observable<boolean> = this.authFunctions._isAuthenticated$.asObservable();
    public handleRedirectCallback$ = this.authFunctions.auth0Client$.pipe(
        take(1), concatMap(client => from(client.handleRedirectCallback()))
    );
    private callback$:Observable<boolean>;
    private loginInProgress = false;
    private destinations = new LoginDestinationStore();
    private sessionGuardWaiters = 0;
    public sessionGuardWaiting$ = new BehaviorSubject<boolean>(false);

    constructor(private router:Router, private ngZone:NgZone, private authFunctions:AuthFunctions,
        private waihonaUserService:WaihonaUserService, private angularFireAuth:AngularFireAuth,
        private conversionService:ConversionService, protected deviceDetectorService:DeviceDetectorService,
        private cookieService:CookieService, protected logger:NGXLogger) {
        // Discard the legacy shared cookie; never migrate it into a new login attempt.
        this.cookieService.delete('loginDestination', '/');
        this.cookieService.delete('loginDestination');
        // Firebase events report identity only. They must not launch competing restores or Auth0 redirects.
        firebase.auth().onAuthStateChanged(state => {
            this.authFunctions.prevAuthState = this.authFunctions.currentAuthState;
            this.authFunctions.currentAuthState = state;
            this.authFunctions.firebaseAuthUser.next(state);
        });
        this.localAuthSetup();
    }

    public get isAuthenticatingCurrently$():Observable<boolean> {
        return this.authFunctions._isAuthenticatingCurrently$;
    }
    public get isAuthenticatingCurrently():boolean {
        return this.authFunctions._isAuthenticatingCurrently$.value;
    }
    public get authState():AuthState {
        if (this.currentUser && this.isAuthenticated) { return AuthState.loggedIn; }
        return this.isHandlingLocalSelfAuth || this.isAuthenticatingCurrently ? AuthState.loggingIn : AuthState.loggedOut;
    }
    public get isAuthenticated():boolean { return this.authFunctions._isAuthenticated$.value; }
    public get isAuthenticated$():BehaviorSubject<boolean> { return this.authFunctions._isAuthenticated$; }
    public get currentUser$():Observable<WaihonaUser> { return this.authFunctions._currentUser$; }
    public get currentUser():WaihonaUser { return this.authFunctions._currentUser$.value; }
    public get isHandlingLocalSelfAuth():boolean { return this.isHandlingLocalSelfAuth$.value; }
    public set isHandlingLocalSelfAuth(value:boolean) { this.isHandlingLocalSelfAuth$.next(value); }
    public get currentUserRef():WaihonaUserRef {
        return this.currentUser ? this.conversionService.convert(this.currentUser, WaihonaUserRef) : null;
    }
    public getUser$(options?):Observable<any> {
        return this.authFunctions.refreshToken(options).pipe(concatMap(success =>
            of(success ? this.authFunctions.userProfileSubject$.value : null)));
    }

    public localAuthSetup():void {
        const params = new URLSearchParams(window.location.search);
        // The callback must consume its code before silent restoration runs.
        if ((window.location.hash || '').startsWith('#/callback') && (params.has('code') || params.has('error'))) { return; }
        this.destinations.clear(); // Abandoned intent cannot survive an ordinary reload.
        this.authFunctions.refreshToken();
    }

    /** Guards use the same full-attempt state as the header, including token exchange and Firebase ack. */
    public async checkIfLoggedIn():Promise<void> {
        if (!this.isHandlingLocalSelfAuth && !this.isAuthenticatingCurrently) { return; }
        this.sessionGuardWaiters++;
        this.sessionGuardWaiting$.next(true);
        try {
            await combineLatest([this.isHandlingLocalSelfAuth$, this.isAuthenticatingCurrently$]).pipe(
                filter(([startup, authenticating]) => !startup && !authenticating), take(1)
            ).toPromise();
        } finally {
            this.sessionGuardWaiters--;
            this.sessionGuardWaiting$.next(this.sessionGuardWaiters > 0);
        }
    }

    private async returnToDestination(attempt:string):Promise<void> {
        const target = this.destinations.consume(attempt) || '/';
        await this.ngZone.run(() => this.router.navigateByUrl(target));
    }

    public async login(redirectPath?:string):Promise<void> {
        if (this.loginInProgress) { return; }
        this.loginInProgress = true;
        let attempt:string;
        let redirected = false;
        try {
            await this.checkIfLoggedIn();
            const destination = this.destinations.safePath(redirectPath || this.router.url || '/');
            attempt = this.destinations.begin(destination);
            this.authFunctions._isAuthenticatingCurrently$.next(true);
            const client = await this.authFunctions.auth0Client$.pipe(take(1)).toPromise();
            if (this.deviceDetectorService.isDesktop()) {
                await client.loginWithPopup({prompt:'login', audience:this.authFunctions._options.audience}, {timeoutInSeconds:120});
                if (!this.destinations.isCurrent(attempt)) { return; }
                const success = await this.authFunctions.refreshToken().pipe(take(1)).toPromise();
                if (success && this.destinations.isCurrent(attempt)) { await this.returnToDestination(attempt); }
            } else {
                await client.loginWithRedirect({redirect_uri:this.authFunctions._options.redirect_uri,
                    appState:{target:destination, loginAttempt:attempt}});
                redirected = true;
            }
        } catch (error) {
            this.logger.error('Sign-in did not complete', error);
            this.authFunctions.cancelAuthentication();
        } finally {
            if (attempt && !redirected) { this.destinations.clear(attempt); }
            this.loginInProgress = false;
            // Redirect navigation leaves the page; popup failure must always release the UI.
            if (!this.currentUser) { this.authFunctions._isAuthenticatingCurrently$.next(false); }
        }
    }

    public handleAuthCallback():Observable<boolean> {
        if (!this.callback$) {
            this.authFunctions._isAuthenticatingCurrently$.next(true);
            this.callback$ = defer(async () => {
                try {
                    const client = await this.authFunctions.auth0Client$.pipe(take(1)).toPromise();
                    const callback = await from(client.handleRedirectCallback()).pipe(timeout(this.authFunctions.sessionTimeoutMs)).toPromise();
                    this.clearCallbackParameters();
                    const success = await this.authFunctions.refreshToken().pipe(take(1)).toPromise();
                    if (success) { await this.returnToDestination(callback.appState?.loginAttempt); }
                    else { this.destinations.clear(); await this.router.navigateByUrl('/'); }
                    return success;
                } catch (error) {
                    this.logger.error('Sign-in callback did not complete', error);
                    this.destinations.clear();
                    this.clearCallbackParameters();
                    this.authFunctions.cancelAuthentication();
                    await this.router.navigateByUrl('/');
                    return false;
                }
            }).pipe(shareReplay(1));
            this.callback$.subscribe({error: error => { this.logger.error('Callback recovery failed', error); this.authFunctions.cancelAuthentication(); }});
        }
        return this.callback$;
    }

    private clearCallbackParameters():void {
        const url = new URL(window.location.href);
        ['code','state','error','error_description'].forEach(key => url.searchParams.delete(key));
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }

    public logout():void { this.destinations.clear(); this.authFunctions.logout(); }
}

export enum AuthState {
    loggedOut='loggedOut', loggingIn='loggingIn', loggedIn='loggedIn'
}
